import { useEffect, useId, useRef, useState } from 'react';
import { CameraCaptureModal } from './CameraCaptureModal';
import { Icon, type IconName } from './Icon';

/**
 * Card-scan source sheet (R14 B6 → R23 C2).
 *
 * Field learning (dev-75): a single "Scan card" button that jumps straight to a file/camera picker
 * is ambiguous on a kiosk tablet — the visitor doesn't know whether it'll open the camera, their
 * photos, or files. So we offer a small three-option source sheet:
 *   - Take photo    → an IN-APP camera surface (R23 C2 — `getUserMedia` live preview + capture)
 *   - Photo library → the gallery (no capture attribute)
 *   - Browse files  → any image/pdf file
 * All three feed the SAME downstream scan flow (`onFile`) — this component only chooses the source.
 *
 * R23 C2 (dev-89 turns 35–48): on the kiosk device "Take photo" used to be a hidden
 * `<input capture="environment">`, but desktop-class / kiosk browsers IGNORE the `capture` attribute
 * and opened a FILE PICKER instead. "Take photo" now opens `CameraCaptureModal` (a real in-app camera)
 * — with a graceful fall-back to the Photo-library picker (permission denied / no camera / unsupported).
 *
 * Reusable across every card-scan surface (welcome S1, close S7, control-bar). Presentation only,
 * tokens + Icon primitive + .reveal motion (reduced-motion safe). The trigger is a render-prop so
 * each host keeps its own button styling; the sheet is a token-styled popover anchored to it.
 */

type Source = 'camera' | 'library' | 'files';

type SourceOption = {
  source: Source;
  label: string;
  hint: string;
  icon: IconName;
};

const OPTIONS: readonly SourceOption[] = [
  {
    source: 'camera',
    label: 'Take photo',
    hint: 'Open the camera',
    icon: 'camera',
  },
  {
    source: 'library',
    label: 'Photo library',
    hint: 'Choose an existing photo',
    icon: 'image',
  },
  {
    source: 'files',
    label: 'Browse files',
    hint: 'Pick an image or PDF',
    icon: 'grid',
  },
];

type Props = {
  /** The chosen file, from whichever source — feeds the host's existing scan flow. */
  onFile: (file: File) => void;
  /** Render-prop trigger: `open` toggles the sheet, `busy`/`disabled` reflect host state. */
  children: (args: { open: () => void; disabled: boolean }) => React.ReactNode;
  /** Disable the trigger (e.g. while a scan is in flight). */
  disabled?: boolean;
  /** Optional heading for the sheet (defaults to a neutral prompt). */
  title?: string;
  /** Render the anchor as a full-width block (e.g. the welcome hero button) instead of inline. */
  block?: boolean;
  /**
   * R25 — called with `true` when ANY scan surface (the source sheet OR the in-app camera modal)
   * becomes visible, and `false` when the last one closes (incl. cancel / capture / away-tap). The
   * host wires this to `useVoice.setScanSurfaceActive`, which emits the §38 `card-modal {open}` signal
   * AND mutes the local mic for the duration of the scan (so the kiosk's own labels/audio never reach
   * STT — the "Shashank Desai" transcript-pollution finding). A single flag across BOTH surfaces means
   * the sheet→camera transition never bounces the signal or the mic (`useVoice` dedups too).
   */
  onScanSurfaceChange?: (active: boolean) => void;
};

export function CardScanSource({
  onFile,
  children,
  disabled = false,
  title,
  block = false,
  onScanSurfaceChange,
}: Props) {
  const [open, setOpen] = useState(false);
  // R23 C2 — the in-app camera modal (Take photo). Separate from the two hidden file inputs, which
  // still back Photo-library and Browse-files (and the camera modal's fallback route).
  const [showCamera, setShowCamera] = useState(false);
  const sheetId = useId();
  // One hidden input per file source (library / files). The camera no longer uses a hidden
  // `capture` input — the kiosk browser ignored it (dev-89) — it opens CameraCaptureModal instead.
  const libraryRef = useRef<HTMLInputElement | null>(null);
  const filesRef = useRef<HTMLInputElement | null>(null);

  // R25 — the scan surface is "active" while EITHER the source sheet OR the in-app camera modal is up.
  // (The two file-input sources — library / files — open a native OS picker that suspends the page,
  // so they don't need gating; the camera modal, which keeps the page live with a getUserMedia stream,
  // does.) Report the derived active-state to the host on every edge; the host mutes the mic + emits
  // the §38 signal for the duration. Cleanup fires a final `false` if the component unmounts mid-scan
  // so a scan surface can never leave the mic muted / the server thinking a modal is still open.
  const scanSurfaceActive = open || showCamera;
  // Mirror the active-state + the latest callback into refs the effects read — so neither effect needs
  // `onScanSurfaceChange` in its deps (hosts pass a stable `useCallback`, but the ref makes that moot:
  // the active-state effect fires on surface EDGES only, never on a callback-identity change).
  const scanSurfaceActiveRef = useRef(scanSurfaceActive);
  scanSurfaceActiveRef.current = scanSurfaceActive;
  const onScanSurfaceChangeRef = useRef(onScanSurfaceChange);
  onScanSurfaceChangeRef.current = onScanSurfaceChange;
  // Report every surface active-state edge to the host (which mutes the mic + emits the §38 signal).
  useEffect(() => {
    onScanSurfaceChangeRef.current?.(scanSurfaceActive);
  }, [scanSurfaceActive]);
  // Safety net: if the component unmounts while a scan surface is still up, close the gate so the mic
  // can't stay muted and the server can't think a modal is still open. Runs on unmount only.
  useEffect(() => {
    return () => {
      if (scanSurfaceActiveRef.current) onScanSurfaceChangeRef.current?.(false);
    };
  }, []);

  function pick(source: Source) {
    setOpen(false);
    if (source === 'camera') {
      // Open the in-app camera surface (R23 C2), not a file picker.
      setShowCamera(true);
      return;
    }
    (source === 'library' ? libraryRef : filesRef).current?.click();
  }

  /** Camera modal fallback (permission denied / no camera / unsupported) → the Photo-library picker. */
  function useLibraryFallback() {
    setShowCamera(false);
    libraryRef.current?.click();
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (f) onFile(f);
  }

  return (
    <div className={block ? 'relative flex w-full' : 'relative inline-flex'}>
      {children({ open: () => setOpen((o) => !o), disabled })}

      {open && (
        <>
          {/* Click-away scrim — dismisses the sheet without capturing a source. */}
          <button
            type="button"
            aria-label="Close"
            className="fixed inset-0 z-[var(--z-overlay)] cursor-default bg-transparent"
            onClick={() => setOpen(false)}
          />
          <div
            id={sheetId}
            role="menu"
            aria-label={title ?? 'Choose a source'}
            className="reveal absolute bottom-full left-0 z-[calc(var(--z-overlay)+1)] mb-2 w-60 rounded-xl bg-elevated ring-1 ring-line-strong shadow-soft p-1.5"
          >
            {title && (
              <p className="px-2.5 pt-1.5 pb-1 text-[11px] uppercase tracking-[0.14em] text-faint">
                {title}
              </p>
            )}
            {OPTIONS.map((opt) => (
              <button
                key={opt.source}
                type="button"
                role="menuitem"
                onClick={() => pick(opt.source)}
                className="flex w-full items-center gap-3 rounded-lg px-2.5 py-2.5 text-left transition-colors duration-1 ease-physical hover:bg-fill min-h-[44px]"
              >
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-brass-soft text-brass">
                  <Icon name={opt.icon} size={17} />
                </span>
                <span className="flex flex-col">
                  <span className="text-sm text-ink">{opt.label}</span>
                  <span className="text-[11px] text-subtle">{opt.hint}</span>
                </span>
              </button>
            ))}
          </div>
        </>
      )}

      {/* R23 C2 — the in-app camera modal (Take photo). Captured frame → the same `onFile` flow a
          picked file uses; fallback (denied / no camera / unsupported) routes to the library picker. */}
      {showCamera && (
        <CameraCaptureModal
          onCapture={(f) => {
            setShowCamera(false);
            onFile(f);
          }}
          onUseFilePicker={useLibraryFallback}
          onClose={() => setShowCamera(false)}
        />
      )}

      {/* Hidden inputs — Photo library (gallery) + Browse files (image/pdf). */}
      <input
        ref={libraryRef}
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={handleChange}
      />
      <input
        ref={filesRef}
        type="file"
        accept="image/*,application/pdf"
        className="sr-only"
        onChange={handleChange}
      />
    </div>
  );
}
